import React, { useState, useMemo } from 'react';
import { useApp } from '../context/AppContext';
import { Calendar, Download, FileText, ArrowUpCircle, ArrowDownCircle, Filter } from 'lucide-react';
import jsPDF from 'jspdf';

const TransactionHistory = () => {
  const { state } = useApp();
  const { transactions } = state;
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const filteredTransactions = useMemo(() => {
    if (!startDate && !endDate) return transactions;
    
    return transactions.filter(transaction => {
      const transactionDate = new Date(transaction.date);
      const start = startDate ? new Date(startDate) : null;
      const end = endDate ? new Date(endDate) : null;
      
      if (start && end) {
        return transactionDate >= start && transactionDate <= end;
      } else if (start) {
        return transactionDate >= start;
      } else if (end) {
        return transactionDate <= end;
      }
      return true;
    });
  }, [transactions, startDate, endDate]);

  const getTransactionIcon = (type) => {
    return type === 'Deposit' ? (
      <ArrowUpCircle className="w-5 h-5 text-green-600" />
    ) : (
      <ArrowDownCircle className="w-5 h-5 text-red-600" />
    );
  };

  const getTransactionBadge = (type) => {
    return type === 'Deposit' ? (
      <span className="px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
        Deposit
      </span>
    ) : (
      <span className="px-3 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
        Withdraw
      </span>
    );
  };

  const downloadPDF = () => {
    const doc = new jsPDF();
    
    // Add title
    doc.setFontSize(20);
    doc.text('Transaction History Report', 20, 20);
    
    // Add date range
    doc.setFontSize(12);
    const dateRange = startDate && endDate 
      ? `From ${new Date(startDate).toLocaleDateString()} to ${new Date(endDate).toLocaleDateString()}`
      : 'All Transactions';
    doc.text(dateRange, 20, 35);
    
    // Add transaction details
    doc.setFontSize(10);
    let yPosition = 55;
    
    // Add headers
    doc.setFont(undefined, 'bold');
    doc.text('Date', 20, yPosition);
    doc.text('Name', 50, yPosition);
    doc.text('Mobile', 90, yPosition);
    doc.text('Amount', 130, yPosition);
    doc.text('Type', 160, yPosition);
    doc.text('Commission', 180, yPosition);
    
    yPosition += 10;
    doc.setFont(undefined, 'normal');
    
    // Add transaction data
    filteredTransactions.forEach((transaction, index) => {
      if (yPosition > 270) { // Start new page if needed
        doc.addPage();
        yPosition = 20;
      }
      
      doc.text(new Date(transaction.date).toLocaleDateString(), 20, yPosition);
      doc.text(transaction.fullName.substring(0, 15), 50, yPosition);
      doc.text(transaction.mobileNumber, 90, yPosition);
      doc.text(`₹${parseFloat(transaction.amount).toLocaleString()}`, 130, yPosition);
      doc.text(transaction.transactionType, 160, yPosition);
      doc.text(`₹${parseFloat(transaction.commission || 0).toLocaleString()}`, 180, yPosition);
      
      yPosition += 8;
    });
    
    // Add summary
    const totalDeposits = filteredTransactions
      .filter(t => t.transactionType === 'Deposit')
      .reduce((sum, t) => sum + parseFloat(t.amount), 0);
    
    const totalWithdrawals = filteredTransactions
      .filter(t => t.transactionType === 'Withdraw')
      .reduce((sum, t) => sum + parseFloat(t.amount), 0);
    
    yPosition += 20;
    doc.setFont(undefined, 'bold');
    doc.text(`Total Deposits: ₹${totalDeposits.toLocaleString()}`, 20, yPosition);
    doc.text(`Total Withdrawals: ₹${totalWithdrawals.toLocaleString()}`, 20, yPosition + 10);
    doc.text(`Net Amount: ₹${(totalDeposits - totalWithdrawals).toLocaleString()}`, 20, yPosition + 20);
    
    // Save the PDF
    const fileName = `transaction-history-${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(fileName);
  };

  const downloadCSV = () => {
    const headers = ['Date', 'Name', 'Mobile', 'Aadhaar', 'Amount', 'Type', 'Commission'];
    const csvData = filteredTransactions.map(transaction => [
      new Date(transaction.date).toLocaleDateString(),
      transaction.fullName,
      transaction.mobileNumber,
      `****${transaction.aadhaarLast4}`,
      parseFloat(transaction.amount),
      transaction.transactionType,
      parseFloat(transaction.commission || 0)
    ]);
    
    const csvContent = [headers, ...csvData]
      .map(row => row.map(field => `"${field}"`).join(','))
      .join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `transaction-history-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const clearFilters = () => {
    setStartDate('');
    setEndDate('');
  };

  return (
    <div className="p-8">
      <div className="flex justify-between items-center mb-8">
        <div className="flex items-center space-x-4">
          <div className="w-12 h-12 bg-gradient-to-r from-pink-500 to-purple-500 rounded-xl flex items-center justify-center">
            <Calendar className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-4xl font-bold gradient-text">Transaction History</h1>
            <p className="text-gray-600 text-lg mt-1">Filter and export transaction records</p>
          </div>
        </div>
      </div>

      {/* Filter Section */}
      <div className="card mb-8">
        <div className="flex items-center space-x-2 mb-4">
          <Filter className="w-5 h-5 text-gray-600" />
          <h2 className="text-lg font-semibold text-gray-900">Filter Transactions</h2>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Start Date
            </label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="input-field"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              End Date
            </label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="input-field"
            />
          </div>
          
          <div className="flex items-end space-x-2">
            <button
              onClick={clearFilters}
              className="btn-secondary"
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      {/* Results Section */}
      <div className="card">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              Transaction Results ({filteredTransactions.length})
            </h2>
            {(startDate || endDate) && (
              <p className="text-sm text-gray-600 mt-1">
                {startDate && endDate 
                  ? `From ${new Date(startDate).toLocaleDateString()} to ${new Date(endDate).toLocaleDateString()}`
                  : startDate 
                    ? `From ${new Date(startDate).toLocaleDateString()}`
                    : `Until ${new Date(endDate).toLocaleDateString()}`
                }
              </p>
            )}
          </div>
          
          {filteredTransactions.length > 0 && (
            <div className="flex space-x-2">
              <button
                onClick={downloadPDF}
                className="btn-primary"
              >
                <FileText className="w-4 h-4 mr-2" />
                Download PDF
              </button>
              <button
                onClick={downloadCSV}
                className="btn-secondary"
              >
                <Download className="w-4 h-4 mr-2" />
                Download CSV
              </button>
            </div>
          )}
        </div>

        {filteredTransactions.length > 0 ? (
          <>
            {/* Desktop Table View */}
            <div className="hidden lg:block overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Mobile
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Aadhaar
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Amount
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Commission
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredTransactions.map((transaction) => (
                    <tr key={transaction.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {new Date(transaction.date).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{transaction.fullName}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {transaction.mobileNumber}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        ****{transaction.aadhaarLast4}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">
                        ₹{parseFloat(transaction.amount).toLocaleString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center space-x-2">
                          {getTransactionIcon(transaction.transactionType)}
                          {getTransactionBadge(transaction.transactionType)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        ₹{parseFloat(transaction.commission || 0).toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Card View */}
            <div className="lg:hidden space-y-4">
              {filteredTransactions.map((transaction) => (
                <div key={transaction.id} className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex items-center space-x-3">
                      {getTransactionIcon(transaction.transactionType)}
                      <div>
                        <h3 className="font-semibold text-gray-900">{transaction.fullName}</h3>
                        <p className="text-sm text-gray-600">{transaction.mobileNumber}</p>
                      </div>
                    </div>
                    {getTransactionBadge(transaction.transactionType)}
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <span className="text-gray-500">Date:</span>
                      <p className="font-medium">{new Date(transaction.date).toLocaleDateString()}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">Aadhaar:</span>
                      <p className="font-medium">****{transaction.aadhaarLast4}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">Amount:</span>
                      <p className="font-semibold text-lg">₹{parseFloat(transaction.amount).toLocaleString()}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">Commission:</span>
                      <p className="font-medium">₹{parseFloat(transaction.commission || 0).toLocaleString()}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Summary Section */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-8 pt-6 border-t border-gray-200">
              <div className="text-center">
                <p className="text-sm text-gray-600">Total Transactions</p>
                <p className="text-2xl font-bold text-blue-600">{filteredTransactions.length}</p>
              </div>
              <div className="text-center">
                <p className="text-sm text-gray-600">Total Deposits</p>
                <p className="text-2xl font-bold text-green-600">
                  ₹{filteredTransactions
                    .filter(t => t.transactionType === 'Deposit')
                    .reduce((sum, t) => sum + parseFloat(t.amount), 0)
                    .toLocaleString()}
                </p>
              </div>
              <div className="text-center">
                <p className="text-sm text-gray-600">Total Withdrawals</p>
                <p className="text-2xl font-bold text-red-600">
                  ₹{filteredTransactions
                    .filter(t => t.transactionType === 'Withdraw')
                    .reduce((sum, t) => sum + parseFloat(t.amount), 0)
                    .toLocaleString()}
                </p>
              </div>
              <div className="text-center">
                <p className="text-sm text-gray-600">Net Amount</p>
                <p className="text-2xl font-bold text-purple-600">
                  ₹{(filteredTransactions
                    .filter(t => t.transactionType === 'Deposit')
                    .reduce((sum, t) => sum + parseFloat(t.amount), 0) -
                    filteredTransactions
                    .filter(t => t.transactionType === 'Withdraw')
                    .reduce((sum, t) => sum + parseFloat(t.amount), 0)
                  ).toLocaleString()}
                </p>
              </div>
            </div>
          </>
        ) : (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Calendar className="w-8 h-8 text-gray-400" />
            </div>
            <p className="text-gray-500 text-lg">
              {startDate || endDate 
                ? "No transactions found for the selected date range." 
                : "No transactions available."
              }
            </p>
            {(startDate || endDate) && (
              <button
                onClick={clearFilters}
                className="btn-primary mt-4"
              >
                Clear Filters
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default TransactionHistory;
